// ============================================================================
// Doctype Admin Epic (#347) A6 (#354) — pure custom-field identifier validator.
//
// This file is intentionally dependency-free (no drizzle, no D1, no React) so
// it can be imported from:
//   - the admin UI (live feedback as the user types a field name)
//   - the custom-field service (belt-and-braces check before the INSERT)
//   - unit tests (no DB setup required)
//
// Uniqueness (per doctype + company) is NOT checked here — that requires a
// DB round-trip and is enforced by the `idx_custom_fields_company_doctype_field`
// UNIQUE index + the route's 409 translation in `modules/core/routes/custom-fields.ts`.
// ============================================================================

/** Maximum identifier length — leaves headroom under SQLite's 64-char soft cap
 *  and matches the `doctype-register` limits for consistency. */
export const MAX_FIELD_NAME_LENGTH = 64;

/** snake_case: must start with a lowercase letter, then lowercase letters,
 *  digits, or underscores. Intentionally rejects leading digits/underscores
 *  and uppercase — matches the rest of the kubdee schema naming convention
 *  (see doctype-register.ts + rules/module-table-prefix.md). */
const SNAKE_CASE_RE = /^[a-z][a-z0-9_]*$/;

/** Reserved words that would clash with the runtime shape we store custom
 *  values under (`row.custom_data[fieldName]`) OR with Drizzle's base-column
 *  names on every tenant table (see `createTimestamps`/`updateTimestamps`).
 *
 *  Keeping this list conservative: we only block names that would actively
 *  cause bugs (e.g. a field named `id` would shadow the row PK when merged
 *  into the form state). Customers can name their field `customer_id` or
 *  `created_at_note` — both snake_case, neither on the reserved list.
 */
export const RESERVED_FIELD_NAMES: readonly string[] = [
  "id",
  "company_id",
  "created_at",
  "updated_at",
  "created_by",
  "updated_by",
  "deleted_at",
  "deleted_by",
  "custom_data",
  // JS/TS runtime foot-guns — these would collide with Object.prototype when
  // iterating custom_data, causing hard-to-debug `in`/`hasOwnProperty` misses.
  "__proto__",
  "constructor",
  "prototype",
];

export interface ValidateFieldNameResult {
  valid: boolean;
  /** Machine-readable reason code — stable across versions, safe to switch on. */
  code?:
    | "EMPTY"
    | "TOO_LONG"
    | "NOT_SNAKE_CASE"
    | "RESERVED"
    | "INVALID_TYPE";
  /** Human-readable reason (English — tests assert against it; UI layer
   *  maps the `code` to a Thai message for end users). */
  reason?: string;
}

/**
 * Validate a custom-field identifier in isolation.
 *
 * Pure, synchronous, side-effect-free. Does NOT check uniqueness — that lives
 * in the DB layer (UNIQUE index + 409 response). Callers should chain:
 *   1. `validateCustomFieldName(name)` — shape validation
 *   2. server round-trip — uniqueness enforcement
 */
export function validateCustomFieldName(
  name: unknown,
): ValidateFieldNameResult {
  if (typeof name !== "string") {
    return {
      valid: false,
      code: "INVALID_TYPE",
      reason: "field name must be a string",
    };
  }

  const trimmed = name.trim();

  if (trimmed.length === 0) {
    return {
      valid: false,
      code: "EMPTY",
      reason: "field name is required",
    };
  }

  if (trimmed.length > MAX_FIELD_NAME_LENGTH) {
    return {
      valid: false,
      code: "TOO_LONG",
      reason: `field name must be at most ${MAX_FIELD_NAME_LENGTH} characters (got ${trimmed.length})`,
    };
  }

  // Reserved-word check runs BEFORE the snake_case shape check on purpose.
  // A couple of reserved names (`__proto__`, `constructor`) are not
  // snake_case in the strict sense (leading underscore / uppercase), but
  // they're the most security-sensitive names on the list — reporting
  // RESERVED here gives the UI a clearer error message and prevents a
  // "NOT_SNAKE_CASE" hint from accidentally steering the user toward a
  // prototype-pollution payload.
  if (RESERVED_FIELD_NAMES.includes(trimmed)) {
    return {
      valid: false,
      code: "RESERVED",
      reason: `"${trimmed}" is reserved and cannot be used as a field name`,
    };
  }

  if (!SNAKE_CASE_RE.test(trimmed)) {
    return {
      valid: false,
      code: "NOT_SNAKE_CASE",
      reason:
        "field name must be snake_case — start with a lowercase letter, then lowercase letters, digits, or underscores only",
    };
  }

  return { valid: true };
}

/**
 * Derive a safe snake_case field name from a human-readable label.
 *
 * This MIRRORS the slug shape expected by `validateCustomFieldName`, so the
 * result of `slugifyFieldName(label)` is guaranteed to pass validation IFF
 * the label contained at least one ASCII letter. Labels with only Thai /
 * non-Latin characters return an empty string — the caller MUST validate
 * before sending.
 *
 * We keep this separate from the existing `slugifyFieldName` in
 * `custom-field.service.ts` (which preserves Thai codepoints for display)
 * because the new tab's "auto-fill fieldname from label" needs an ASCII-only
 * identifier — `custom_fields.field_name` is used as a JS object key and Thai
 * keys break JSON serialization in several downstream consumers.
 */
export function deriveFieldNameFromLabel(label: string): string {
  const slug = label
    .trim()
    .toLowerCase()
    // Replace any run of non-ASCII-alnum with a single underscore. This is
    // the only place we drop Thai codepoints; the label itself is stored
    // untouched so end users see their Thai text verbatim in the UI.
    .replace(/[^a-z0-9]+/g, "_")
    // Strip leading / trailing underscores and collapse runs.
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");

  // If the derived slug starts with a digit, prefix `f_` so it passes the
  // `starts-with-letter` rule. Callers can edit the field name freely if
  // they don't like the fallback.
  if (slug.length > 0 && /^[0-9]/.test(slug)) {
    return `f_${slug}`;
  }

  // Truncate to the validator's cap so downstream validation doesn't fail
  // with TOO_LONG on the derived value.
  if (slug.length > MAX_FIELD_NAME_LENGTH) {
    return slug.slice(0, MAX_FIELD_NAME_LENGTH).replace(/_+$/, "");
  }

  return slug;
}
